// MSMESupportSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import {
    FaHandshake,
    FaGlobe,
    FaUsers,
    FaRocket,
    FaIndustry,
    FaCogs,
    FaMicrochip,
    FaBolt,
    FaRobot,
    FaCar,
    FaSun,
    FaCode,
    FaChartLine,
    FaShieldAlt,
    FaLightbulb,
    FaNetworkWired,
    FaBalanceScale,
    FaSeedling,
    FaTrophy,
    FaAward,
    FaRegBuilding,
    FaWarehouse,
    FaShippingFast,
    FaTools
} from 'react-icons/fa';
import {
    SiSpacex,
    SiTesla,
    SiRenault,
    SiNvidia,
    SiIntel
} from 'react-icons/si';

const MSMESupportSection = () => {
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
    const controls = useAnimation();
    const [activeIndustry, setActiveIndustry] = useState(null);
    const [schemeCount, setSchemeCount] = useState(0);

    const themeColor = "rgb(43 147 208)";
    const themeColorDark = "rgb(32 110 156)";
    const themeColorLight = "rgba(43, 147, 208, 0.1)";

    useEffect(() => {
        if (isInView) {
            controls.start("visible");

            // Animate counter
            const timer = setTimeout(() => {
                let count = 0;
                const interval = setInterval(() => {
                    count += 1;
                    setSchemeCount(count);
                    if (count >= 25) clearInterval(interval);
                }, 60);
            }, 1000);
        }
    }, [isInView, controls]);

    const industries = [
        {
            name: "Aerospace",
            icon: SiSpacex,
            color: "text-purple-600",
            bgColor: "bg-purple-50",
            description: "Aviation and space technology"
        },
        {
            name: "Mechanical",
            icon: FaCogs,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            description: "Machinery and manufacturing"
        },
        {
            name: "Electrical",
            icon: FaBolt,
            color: "text-yellow-600",
            bgColor: "bg-yellow-50",
            description: "Power systems and electronics"
        },
        {
            name: "Electronics",
            icon: FaMicrochip,
            color: "text-green-600",
            bgColor: "bg-green-50",
            description: "Circuit design and components"
        },
        {
            name: "Chemical",
            icon: FaIndustry,
            color: "text-red-600",
            bgColor: "bg-red-50",
            description: "Chemical processing"
        },
        {
            name: "Textile",
            icon: FaRegBuilding,
            color: "text-pink-600",
            bgColor: "bg-pink-50",
            description: "Fabric and garment manufacturing"
        },
        {
            name: "Food Processing",
            icon: FaSeedling,
            color: "text-emerald-600",
            bgColor: "bg-emerald-50",
            description: "Agricultural products"
        },
        {
            name: "Ceramic",
            icon: FaWarehouse,
            color: "text-orange-600",
            bgColor: "bg-orange-50",
            description: "Clay and ceramic products"
        },
        {
            name: "Wood",
            icon: FaTools,
            color: "text-amber-900",
            bgColor: "bg-amber-50",
            description: "Timber and furniture"
        },
        {
            name: "Digital Infrastructure",
            icon: FaNetworkWired,
            color: "text-cyan-600",
            bgColor: "bg-cyan-50",
            description: "IT infrastructure"
        },
        {
            name: "E-Commerce",
            icon: FaShippingFast,
            color: "text-indigo-600",
            bgColor: "bg-indigo-50",
            description: "Online retail platforms"
        },
        {
            name: "AI & ML",
            icon: FaRobot,
            color: "text-violet-600",
            bgColor: "bg-violet-50",
            description: "Artificial Intelligence"
        },
        {
            name: "Robotics",
            icon: SiTesla,
            color: "text-rose-600",
            bgColor: "bg-rose-50",
            description: "Automation systems"
        },
        {
            name: "Electric Vehicle",
            icon: FaCar,
            color: "text-teal-600",
            bgColor: "bg-teal-50",
            description: "EV manufacturing"
        },
        {
            name: "Renewable Energy",
            icon: FaSun,
            color: "text-yellow-500",
            bgColor: "bg-yellow-50",
            description: "Solar and wind energy"
        },
        {
            name: "Software Development",
            icon: FaCode,
            color: "text-blue-700",
            bgColor: "bg-blue-50",
            description: "Software solutions"
        },
        {
            name: "Hardware",
            icon: SiIntel,
            color: "text-gray-700",
            bgColor: "bg-gray-50",
            description: "Computer hardware"
        }
    ];

    const initiatives = [
        {
            title: "Regulatory Framework Development",
            description: "Addressing policy gaps and creating favorable regulations for MSMEs",
            icon: FaBalanceScale
        },
        {
            title: "Global Market Access",
            description: "Opening international trade opportunities for MSMEs",
            icon: FaGlobe
        },
        {
            title: "Technology Adoption",
            description: "Facilitating digital transformation and Industry 4.0 integration",
            icon: FaRocket
        },
        {
            title: "Financial Support",
            description: "Connecting MSMEs with funding and investment opportunities",
            icon: FaChartLine
        }
    ];

  
    

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    const scaleIn = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: {
            scale: 1,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    return (
        <section
            ref={sectionRef}
            className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden"
        >
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{
                        x: [0, 100, 0],
                        y: [0, 50, 0],
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-30"
                ></motion.div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-20"></div>
            </div>

            <div className="relative max-w-7xl mx-auto">
                {/* Header */}
           
                {/* Vision & Mission Cards */}
           

                {/* Our Commitment Section */}
            

                {/* Industries We Support */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={controls}
                    className="mb-20"
                >
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Industries We <span style={{ color: themeColor }}>Support & Empower</span>
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Comprehensive support across diverse sectors driving innovation and economic growth
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {industries.map((industry, index) => {
                            const Icon = industry.icon;
                            return (
                                <motion.div
                                    key={index}
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    onMouseEnter={() => setActiveIndustry(industry.name)}
                                    onMouseLeave={() => setActiveIndustry(null)}
                                    className={`relative cursor-pointer transition-all duration-300 ${activeIndustry === industry.name ? 'z-10' : ''
                                        }`}
                                >
                                    <div
                                        className={`p-4 rounded-xl border-2 transition-all duration-300 ${activeIndustry === industry.name
                                            ? 'border-blue-500 shadow-lg scale-105'
                                            : 'border-gray-200 hover:border-blue-300'
                                            } ${industry.bgColor}`}
                                    >
                                        <div className="flex flex-col items-center text-center">
                                            <Icon className={`w-8 h-8 mb-3 ${industry.color}`} />
                                            <span className="font-semibold text-gray-900 text-sm">
                                                {industry.name}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Government Schemes Counter */}
                <motion.div
                    variants={scaleIn}
                    initial="hidden"
                    animate={controls}
                    className="mb-2"
                >
                    <div
                        className="rounded-2xl p-8 lg:p-12 text-center relative overflow-hidden"
                        style={{
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColorDark} 100%)`,
                            // boxShadow: '0 20px 60px rgba(43, 147, 208, 0.3)'
                        }}
                    >
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 left-0 w-64 h-64 border-4 border-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                            <div className="absolute bottom-0 right-0 w-64 h-64 border-4 border-white rounded-full translate-x-1/2 translate-y-1/2"></div>
                        </div>

                        <div className="relative z-10">
                            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                                <div className="text-center">
                                    <motion.div
                                        className="text-7xl md:text-8xl font-bold text-white mb-2"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 200,
                                            damping: 20,
                                            delay: 0.5
                                        }}
                                    >
                                        {schemeCount}+
                                    </motion.div>
                                    <div className="text-2xl font-semibold text-white">MSME Indian Govt Scheme</div>
                                </div>

                                <div className="hidden md:block w-px h-52 bg-white/30"></div>

                                <div className="text-justify max-w-2xl">
                                  
                                    <p className="text-blue-100 mb-6">
                                     We understand the seriousness of issues of MSMEs , promote regulatory framework to address those issues and faster opportunities for MSMEs across a range of industries, including Aerospace, Mechinical and Electrical, Electronics, Chemical, Textile, Food Processing, Ceramic, Wood, Digital Infrastructure, E-Commerce, Artificial Intelligence, Machine Learning, Robotics, Electric Vehicle (EV), Renewable Energy (RE), Software Development & Hardware.
                                    </p>
                                    <button
                                        className="px-6 py-3 bg-white rounded-lg font-semibold hover:shadow-lg transition-shadow duration-300"
                                        style={{ color: themeColor }}
                                    >
                                        Explore All Schemes →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Schemes Preview */}
            

                {/* Call to Action */}
           
            </div>

            {/* Floating Particles */}
            {[...Array(10)].map((_, i) => (
                <motion.div
                    key={i}
                    animate={{
                        y: [0, -20, 0],
                        x: [0, Math.random() * 20 - 10, 0],
                    }}
                    transition={{
                        duration: 3 + Math.random() * 2,
                        repeat: Infinity,
                        delay: Math.random() * 2
                    }}
                    className="absolute pointer-events-none"
                    style={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: themeColor,
                        opacity: 0.3
                    }}
                />
            ))}
        </section>
    );
};

export default MSMESupportSection;